import { View } from 'react-native';
import i18n from '../../app/i18n';
import { useEffect, useState } from 'react';
import { detectIncognito } from 'src/_shared/utils/detect-incognito/detectIncognito';
import { Icon } from '../icon/Icon';
import { TestIDs } from 'src/constants/testIDs.constants';
import { ICON_NAMES } from '../icon/icon.helper';
import { styles } from './UnsupportedEnvScreen.styles';
import TextLato from '../text/text-lato/TextLato';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';
import { isWeb } from 'src/_shared/utils/isWeb';
import { logWarning } from 'src/_shared/logger';
import { hidePageLoader } from '../page-loader/page-loader-element/PageLoaderElement.utils.web';
import { IsSupportedEnvResult } from 'src/_shared/utils/is-supported-env/isSupportedEnv.types';
import {
  cleanIndexDB,
  isNoAvailableSpaceError,
} from '../global-error-boundary/error-views/no-available-space-error/NoAvailableSpaceError.utils';
import { FocusGroups } from '../focus-provider/FocusProvider.constants';
import { reloadPage } from 'src/_shared/utils/reload-page/reloadPage.web';
import { allowAllExit } from 'src/_shared/utils/prevent-exit/preventExit.web';
import { generateTestID } from 'src/helpers/testIDs.helper';
import { PrimaryButton } from 'src/editor-v3/components/buttons/primary-button/PrimaryButton';
import { BUTTON_LAYOUTS } from 'src/editor-v3/components/buttons/button/Button.constants';
import { Illustration } from '../illustration/Illustration';
import { ILLUSTRATION_NAMES } from '../illustration/illustration.helper';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

const ANIMATION_DURATION = 300;

export const UnsupportedEnvScreen = (isSupportedEnvResult: IsSupportedEnvResult) => {
  const [{ title, description, icon, cta }, setMessages] = useState<{
    title?: string;
    description?: string;
    icon?: {
      Component: React.FC<{ width: number; height: number }>;
      mobileSize: { width: number; height: number };
      desktopSize: { width: number; height: number };
    };
    cta?: string;
  }>({});

  const { isMobile } = useResponsiveScreen();

  useEffect(() => {
    detectIncognito()
      .then(({ isPrivate }) => {
        if (isPrivate) {
          setMessages({
            title: i18n.t('unsupportedEnv:incognitoTitle'),
            description: i18n.t('unsupportedEnv:incognitoDescription'),
            icon: {
              Component: ({ width, height }) => (
                <Icon name={ICON_NAMES.IncognitoModeAlert} width={width} height={height} />
              ),
              mobileSize: { width: 100, height: 100 },
              desktopSize: { width: 100, height: 100 },
            },
          });
        } else {
          throw new Error(isSupportedEnvResult.reason);
        }
      })
      .catch(e => {
        logWarning(e);
        if (isNoAvailableSpaceError({ event: e, isNetworkAvailable: true })) {
          cleanIndexDB();
          setMessages({
            title: i18n.t('unsupportedEnv:noAvailableSpaceTitle'),
            description: i18n.t('unsupportedEnv:noAvailableSpaceDescription'),
            icon: {
              Component: ({ width, height }) => (
                <Illustration name={ILLUSTRATION_NAMES.Alert} width={width} height={height} />
              ),
              mobileSize: { width: 54, height: 54 },
              desktopSize: { width: 64, height: 64 },
            },
            cta: i18n.t('unsupportedEnv:noAvailableSpaceCta'),
          });
        } else {
          setMessages({
            title: i18n.t('unsupportedEnv:title', { env: isWeb() ? 'Browser' : 'App' }),
            description: i18n.t('unsupportedEnv:description', { env: isWeb() ? 'Browser' : 'App' }),
            icon: {
              Component: ({ width, height }) => (
                <Icon name={ICON_NAMES.NotSupportedEnvAlert} width={width} height={height} />
              ),
              mobileSize: { width: 60, height: 60 },
              desktopSize: { width: 60, height: 60 },
            },
          });
        }
      })
      .then(() => {
        hidePageLoader();
      });
  }, []);

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Icon height={32} width={115} testID={`${TestIDs.PHOTOSI_LOGO}-icon`} name={ICON_NAMES.PhotosiLogo} />
      </View>
      <View style={styles.contentWrapper}>
        {(title || description || icon) && (
          <Animated.View
            style={styles.content}
            entering={FadeIn.duration(ANIMATION_DURATION)}
            exiting={FadeOut.duration(ANIMATION_DURATION)}
          >
            {icon && (
              <View style={styles.iconWrapper}>
                <icon.Component
                  width={isMobile ? icon.mobileSize.width : icon.desktopSize.width}
                  height={isMobile ? icon.mobileSize.height : icon.desktopSize.height}
                />
              </View>
            )}
            {title && <TextLato style={styles.title}>{title}</TextLato>}
            {description && <TextLato style={styles.description}>{description}</TextLato>}
            {cta && (
              <PrimaryButton
                style={styles.button}
                onPress={async () => {
                  allowAllExit();
                  reloadPage();
                }}
                text={cta}
                testID={generateTestID(TestIDs.NO_AVAILABLE_SPACE, TestIDs.CONFIRM)}
                focusId={generateTestID(TestIDs.NO_AVAILABLE_SPACE, TestIDs.CONFIRM)}
                focusOrder={0}
                focusGroup={FocusGroups.UNSUPPORTED_ENV}
                layout={BUTTON_LAYOUTS.CENTER}
              />
            )}
          </Animated.View>
        )}
      </View>
    </View>
  );
};
